import React from "react"

import clsx from "clsx"

const badgeTones = [
  `gray`,
  `red`,
  `yellow`,
  `green`,
  `blue`,
  `indigo`,
  `purple`,
  `pink`,
] as const

export type BadgeTone = (typeof badgeTones)[number]
export type BadgeVariant = `flat` | `rounded`

export type BadgeProps = React.ComponentPropsWithoutRef<`span`> & {
  tone?: BadgeTone
  variant?: BadgeVariant
  dot?: boolean
}

type BadgeStyleVariables = Partial<
  Record<`--badge-bg` | `--badge-dot` | `--badge-fg` | `--badge-tone`, string>
> &
  React.CSSProperties

const BADGE_TONE_VALUE: Record<BadgeTone, string> = {
  gray: `var(--powur-gray)`,
  red: `var(--brand-danger)`,
  yellow: `var(--brand-warning)`,
  green: `var(--brand-success)`,
  blue: `var(--powur-blue)`,
  indigo: `var(--powur-navy)`,
  purple: `color-mix(in srgb, var(--powur-blue) 45%, var(--powur-deep-navy) 55%)`,
  pink: `color-mix(in srgb, var(--brand-danger) 72%, var(--powur-cyan) 28%)`,
}

const getBadgeToneStyles = (tone: BadgeTone): BadgeStyleVariables => {
  const badgeTone = BADGE_TONE_VALUE[tone]

  if (tone === `gray`) {
    return {
      "--badge-tone": badgeTone,
      "--badge-bg": `color-mix(in srgb, var(--badge-tone) 14%, var(--color-background))`,
      "--badge-fg": `color-mix(in srgb, var(--badge-tone) 88%, var(--color-foreground) 12%)`,
      "--badge-dot": `color-mix(in srgb, var(--badge-tone) 76%, var(--color-background) 24%)`,
    }
  }

  if (tone === `yellow`) {
    return {
      "--badge-tone": badgeTone,
      "--badge-bg": `color-mix(in srgb, var(--badge-tone) 24%, var(--color-background))`,
      "--badge-fg": `color-mix(in srgb, var(--badge-tone) 30%, var(--color-foreground) 70%)`,
      "--badge-dot": `var(--badge-tone)`,
    }
  }

  return {
    "--badge-tone": badgeTone,
    "--badge-bg": `color-mix(in srgb, var(--badge-tone) 14%, var(--color-background))`,
    "--badge-fg": `color-mix(in srgb, var(--badge-tone) 82%, var(--color-foreground) 18%)`,
    "--badge-dot": `var(--badge-tone)`,
  }
}

export const Badge = React.forwardRef(function Badge(
  {
    tone = `gray`,
    variant = `rounded`,
    dot = false,
    className,
    style,
    children,
    ...props
  }: BadgeProps,
  ref: React.ForwardedRef<HTMLSpanElement>,
) {
  return (
    <span
      {...props}
      ref={ref}
      data-slot="badge"
      data-tone={tone}
      data-variant={variant}
      style={{ ...getBadgeToneStyles(tone), ...style }}
      className={clsx(
        `inline-flex items-center gap-x-1.5 whitespace-nowrap px-2 py-1 font-sans text-xs font-medium`,
        variant === `rounded` ? `rounded-full` : `rounded-md`,
        `bg-[var(--badge-bg)] text-[var(--badge-fg)]`,
        className,
      )}
    >
      {dot ? (
        <svg
          viewBox="0 0 6 6"
          aria-hidden="true"
          data-slot="badge-dot"
          className="size-1.5 shrink-0 fill-[var(--badge-dot)]"
        >
          <circle r={3} cx={3} cy={3} />
        </svg>
      ) : null}
      {children}
    </span>
  )
})
